import React, { useMemo } from 'react';
import { UserRound } from 'lucide-react';
import QuotaSpendCharts from '../components/QuotaSpendCharts';
import { GatewayDrawer } from '../components/GatewayUi';
import { useGovernedMcp } from '../governedMcpStore';
import type { QuotaUser } from '../governedMcpTypes';
import {
  buildEffectiveQuotaUsers,
  clientLabel,
  formatQuotaMonth,
  formatTokens,
  remainingTokens,
  quotaStatus,
  sourceLabel,
  utilizationPercent,
} from '../quotaUsage';

function statusMeta(user: QuotaUser, isLight: boolean): { label: string; tone: 'success' | 'warn' | 'neutral' } {
  const status = quotaStatus(user);
  if (status === 'exceeded') return { label: 'Over limit', tone: 'warn' };
  if (status === 'warning') return { label: `${utilizationPercent(user)}% used`, tone: 'warn' };
  return { label: `${utilizationPercent(user)}% used`, tone: isLight ? 'success' : 'success' };
}

export default function QuotaUserDrawer({
  user,
  clientFilter = 'all',
  month,
  isLight,
  onClose,
}: {
  user: QuotaUser | null;
  clientFilter?: string;
  month: string;
  isLight: boolean;
  onClose: () => void;
}) {
  const { state, quotaDirectory } = useGovernedMcp();
  const monthUsers = useMemo(
    () =>
      buildEffectiveQuotaUsers(state.apiKeys, state.quotaAssignments, state.quotaUsages, quotaDirectory, month),
    [month, quotaDirectory, state.apiKeys, state.quotaAssignments, state.quotaUsages]
  );
  const live = user ? monthUsers.find((item) => item.id === user.id || (item.userId && item.userId === user.userId && item.clientId === user.clientId)) ?? user : null;
  const clientName = live ? clientLabel(live.clientId, state.clients) : '';
  const showAllClients = clientFilter === 'all';
  const personAssignments = useMemo(() => {
    if (!live) return [];
    const samePerson = monthUsers.filter((item) => item.userId && item.userId === live.userId);
    if (!showAllClients) return samePerson.filter((item) => item.clientId === live.clientId);
    return samePerson.length ? samePerson : live ? [live] : [];
  }, [live, monthUsers, showAllClients]);

  const used = personAssignments.reduce((sum, item) => sum + item.tokensUsed, 0);
  const limit = personAssignments.reduce((sum, item) => sum + item.tokenLimit, 0);
  const remaining = personAssignments.reduce((sum, item) => sum + remainingTokens(item), 0);

  return (
    <GatewayDrawer
      open={Boolean(live)}
      title={live?.name || 'User usage'}
      subtitle={
        live
          ? `${live.email} · ${live.role || 'No role'} · ${formatQuotaMonth(month)}${showAllClients ? ' · All clients' : ` · ${clientName}`}`
          : undefined
      }
      eyebrow="Governed MCP / Quotas & Spendings"
      icon={<UserRound size={18} />}
      status={live ? statusMeta(live, isLight) : undefined}
      isLight={isLight}
      onClose={onClose}
      size="lg"
      maximizable
    >
      {live ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: showAllClients ? 'Clients' : 'Client', value: showAllClients ? `${personAssignments.length} assigned` : clientName },
              { label: 'Effective limit', value: formatTokens(limit) },
              { label: 'Current spend', value: formatTokens(used) },
              { label: 'Remaining', value: formatTokens(remaining) },
            ].map((item) => (
              <div
                key={item.label}
                className={`rounded-xl border px-3.5 py-3 ${isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-900'}`}
              >
                <p className={`text-[11px] uppercase tracking-wide ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.label}</p>
                <p className={`mt-1 text-sm font-semibold tabular-nums ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.value}</p>
              </div>
            ))}
          </div>
          <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
            Source: {sourceLabel(live.source)}
          </p>
          <QuotaSpendCharts
            users={personAssignments}
            isLight={isLight}
            subtitle={`${formatQuotaMonth(month)} · ${showAllClients ? 'All clients' : clientName}`}
          />
        </div>
      ) : null}
    </GatewayDrawer>
  );
}
